import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';

import isNotEmptyReactElementContent from 'core/utils/content';
import Text from 'core/components/Text';
import { VIDEO_PROVIDERS } from 'core/site/constants';
import withPuids from 'core/hocs/withPuids';
import { puidsPropTypes } from 'core/utils/prop-types/ads';
import requestHeadersPropTypes from 'core/utils/prop-types/request-headers';
import { parseUrl } from 'core/utils/url-helper';

import { getSrcFromMarkup, getIsShortsFromMarkup, getVideoId } from './utils';

import YoutubeVideo from './YoutubeVideo';
import RamblerVideo from './RamblerVideo';
import Other from './Other';

import styles from './index.styl';

const playerMap = {
  youtube: YoutubeVideo,
  videoplatform: RamblerVideo,
  vk: Other,
  other: Other,
};

/**
 * BaseVideo наследуется в амп версии чтобы получить доступ к методам
 */
export class BaseVideo extends React.PureComponent {
  static propTypes = {
    /** Подпись к видео. */
    caption: PropTypes.node,

    /** Компонент, с помощью которого будет отрисовываться caption. */
    captionComponent: PropTypes.func,

    /**
     * Код видео или ссылка, содержащая его.
     * Для Youtube можно использовать как ссылку из адресной строки браузера, так и embed-ссылку.
     */
    code: PropTypes.string,

    /**
     * Платформа, на которой размещено видео.
     */
    providerName: PropTypes.oneOf(VIDEO_PROVIDERS),

    /**
     * Рекламные метки для таргетирования.
     */
    puids: puidsPropTypes,
  };

  static contextTypes = {
    requestHeaders: requestHeadersPropTypes,
    features: PropTypes.shape({
      disableAutoplayOnFreeSubdomain: PropTypes.bool,
    }),
  };

  static defaultProps = {
    captionComponent: ({ children }) => <Text className={styles.caption}>{children}</Text>,
  };

  get parsedCode() {
    const { code } = this.props;
    const raw = getSrcFromMarkup(code) || code || '';
    return parseUrl(raw);
  }

  get isShorts() {
    const { code, providerName } = this.props;

    return getIsShortsFromMarkup(code, providerName) || false;
  }

  render() {
    const {
      caption,
      captionComponent: CaptionComponent,
      code,
      providerName,
      puids,
    } = this.props;

    if (!(code && providerName)) return null;

    const Player = playerMap[providerName] || playerMap.youtube;
    const videoId = getVideoId(providerName, code, this.parsedCode);

    return (
      <div className={cx(styles.wrapper, this.isShorts && styles._shorts)}>
        <Player
          videoId={videoId}
          puids={puids}
          isShorts={this.isShorts}
        />
        {isNotEmptyReactElementContent(caption) &&
          <CaptionComponent>{caption}</CaptionComponent>
        }
      </div>
    );
  }
}

const BaseVideoWithHOCs = withPuids(BaseVideo);
BaseVideoWithHOCs.displayName = 'BaseVideo';

export default BaseVideoWithHOCs;
